import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LOGO_FULL, LOGO_ROUND, FAVICON } from "@/lib/constants";
import { KeyRound, Home, CalendarCheck, Users, Sparkles, ShieldCheck, Star, ArrowRight } from "lucide-react";

const services = [
  { icon: KeyRound, title: "Sleutelbeheer", desc: "Veilig beheer en overdracht van de sleutels van uw woning." },
  { icon: Home, title: "Woningbeheer", desc: "Wij zijn uw ogen, oren en handen aan de Costa Blanca Noord." },
  { icon: CalendarCheck, title: "Check-in & Check-out", desc: "Persoonlijke ontvangst en begeleiding van uw gasten." },
  { icon: Sparkles, title: "Schoonmaak & Onderhoud", desc: "Uw huis altijd spik en span, klaar voor het volgende verblijf." },
];

const roles = ["Eigenaren", "Gasten", "Medewerkers"];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-border">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="brand-logo">
            <img src={LOGO_FULL} alt="Costa Key" className="h-12 w-auto object-contain" />
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login"><Button className="bg-primary hover:bg-primary/90" data-testid="nav-login-btn">Inloggen</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-5 grid lg:grid-cols-2 gap-10 items-center py-16 lg:py-24">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-secondary bg-secondary/10 px-3 py-1 rounded-full">
              <ShieldCheck className="h-4 w-4" /> Costa Blanca Noord · Spanje
            </span>
            <h1 className="mt-5 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Uw woning in <span className="text-primary">vertrouwde</span> handen
            </h1>
            <p className="mt-5 text-lg text-muted-foreground max-w-md">
              Eén centraal portaal voor eigenaren, gasten en ons team.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/login">
                <Button size="lg" className="bg-primary hover:bg-primary/90 gap-2" data-testid="hero-login-cta-btn">
                  Inloggen op portaal <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
              <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-primary text-primary" />)}</div>
              5,0 · 17 reviews op Google
            </div>
          </div>
          <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <img
              src="/brand/costakey-zomer.webp?v=3"
              alt="Costa Key villa aan de Costa Blanca"
              className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/3]"
              data-testid="landing-hero-image"
            />
            <img src={LOGO_ROUND} alt="Costa Key" className="absolute -top-6 -right-6 h-28 w-28 rounded-full shadow-2xl shadow-primary/30 hidden sm:block animate-float" />
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl border border-border shadow-xl p-4 hidden sm:block">
              <div className="flex items-center gap-3">
                <img src={FAVICON} alt="" className="h-11 w-11 rounded-full shrink-0" />
                <div>
                  <div className="font-display font-bold">7 dagen per week</div>
                  <div className="text-xs text-muted-foreground">Eén vast aanspreekpunt</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="max-w-6xl mx-auto px-5 py-16">
        <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">Onze diensten</h2>
        <p className="text-muted-foreground mt-2">Met passie en zorgvuldigheid zorgen wij voor uw (2e) huis.</p>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s) => (
            <div key={s.title} className="bg-card rounded-xl border border-border p-6 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-200" data-testid={`service-${s.title}`}>
              <span className="grid place-items-center h-11 w-11 rounded-lg bg-primary/10 text-primary"><s.icon className="h-5 w-5" /></span>
              <h3 className="mt-4 font-display font-semibold text-lg">{s.title}</h3>
              <p className="text-sm text-muted-foreground mt-1">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Roles */}
      <section className="bg-secondary/5 border-y border-border">
        <div className="max-w-6xl mx-auto px-5 py-14">
          <div className="flex items-center gap-3">
            <Users className="h-6 w-6 text-secondary" />
            <h2 className="font-display font-bold text-2xl tracking-tight">Voor iedereen één portaal</h2>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            {roles.map((r) => (
              <span key={r} className="bg-white border border-border rounded-full px-4 py-2 text-sm font-medium shadow-sm">{r}</span>
            ))}
          </div>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-5 py-10 text-sm text-muted-foreground flex flex-col sm:flex-row justify-between gap-3">
        <div>© {new Date().getFullYear()} Costa Key · Sleutel- & Woningbeheer</div>
        <a href="https://www.costakey.es" target="_blank" rel="noreferrer" className="hover:text-primary">www.costakey.es</a>
      </footer>
    </div>
  );
}
